// ============================================
// DETECTORDUPLICADOS.JS — Avisa de posibles duplicados mientras el usuario escribe
// Se conecta a GET /api/clientes/buscar-similares con debounce de 500ms.
// ============================================

import React, { useEffect, useRef, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';

const COLOR_ALERTA = '#a86b1e';
const COLOR_ALERTA_BG = '#fff4e5';

export default function DetectorDuplicados({
  tipo,                  // 'grupo' | 'razon_social' | 'sucursal' | 'persona'
  nombreActual,          // string
  contextoId,            // opcional: id del padre (solo relevante para 'sucursal')
  onSeleccionarExistente, // (item) => void — callback al hacer click en "Usar este"
  onCambioResultados,    // (cuantos) => void — notifica al parent cuántos matches hay
}) {
  const [similares, setSimilares] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [colapsado, setColapsado] = useState(false);
  const timerRef = useRef(null);
  const ultimoQueryRef = useRef('');

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = (nombreActual || '').trim();

    if (q.length < 3) {
      setSimilares([]);
      if (onCambioResultados) onCambioResultados(0);
      return;
    }

    timerRef.current = setTimeout(async () => {
      ultimoQueryRef.current = q;
      setCargando(true);
      try {
        const params = new URLSearchParams({ tipo, nombre: q });
        if (contextoId) params.set('contexto_id', String(contextoId));
        const r = await fetch(`${API_BASE_CLI}/api/clientes/buscar-similares?${params}`, { headers: authHeaders() }).then(x => x.json());
        // Ignorar si el usuario siguió escribiendo (la respuesta ya no es vigente)
        if (ultimoQueryRef.current !== q) return;
        if (r.ok) {
          setSimilares(r.similares || []);
          if (onCambioResultados) onCambioResultados((r.similares || []).length);
        }
      } catch (e) {
        // best-effort: si falla, simplemente no mostramos nada
        setSimilares([]);
        if (onCambioResultados) onCambioResultados(0);
      } finally {
        setCargando(false);
      }
    }, 500);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [tipo, nombreActual, contextoId]);

  if (similares.length === 0) return null;

  return (
    <div style={{
      marginTop: 10, background: COLOR_ALERTA_BG, border: `1px solid #f0d9b8`,
      borderRadius: 8, padding: colapsado ? '8px 12px' : '10px 12px', fontFamily: PP,
    }}>
      <div
        onClick={() => setColapsado(c => !c)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
      >
        <span style={{ fontSize: 14 }}>⚠️</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: COLOR_ALERTA, flex: 1 }}>
          Encontramos {similares.length} similar{similares.length === 1 ? '' : 'es'}. ¿No es alguno de estos?
        </span>
        <span style={{ fontSize: 10, color: COLOR_ALERTA }}>{colapsado ? '▸' : '▾'}</span>
      </div>

      {!colapsado && (
        <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
          {similares.map(s => (
            <div key={`${s.tipo}-${s.id}`} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              background: '#fff', borderRadius: 6,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.nombre}
                </div>
                {s.contexto && (
                  <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                    {s.contexto}
                  </div>
                )}
              </div>
              <button
                onClick={() => onSeleccionarExistente(s)}
                style={{
                  background: COLOR_PRINCIPAL, color: '#fff', border: 'none',
                  padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: PP, flexShrink: 0,
                }}
              >
                Usar este
              </button>
            </div>
          ))}
          {cargando && <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>Buscando…</div>}
        </div>
      )}
    </div>
  );
}
